// Arqum 2026-10-19.
(() => {
'use strict';
const CONTEXT_ATTRIBUTE = '__ips_meta_confirmed_context_v1';
const CONTEXT_SHOP = 'wdpa1m-fi.myshopify.com';
const CONTEXT_TTL_MS = 30 * 60 * 1000;
const CONTEXT_WRITE_LOCK = `ips-ad-summary-write:${CONTEXT_SHOP}:v1`;
const MAX_CONTEXT_BYTES = 3072;

function allowedHost(env) {
  return env.location?.protocol === 'https:' && ['ipshealth.co.za', 'www.ipshealth.co.za'].includes(env.location?.hostname) && env.location?.origin === `https://${env.location.hostname}`;
}

function readStorefrontPermission(env) {
  try {
    const api = env.Shopify.customerPrivacy;
    const marketingAllowed = api.marketingAllowed() === true;
    const saleOfDataAllowed = api.saleOfDataAllowed() === true;
    const choice = typeof api.currentVisitorConsent === 'function' ? api.currentVisitorConsent() : {};
    return { marketingAllowed: marketingAllowed && choice?.marketing !== 'no', saleOfDataAllowed: saleOfDataAllowed && choice?.sale_of_data !== 'no' };
  } catch { return { marketingAllowed: false, saleOfDataAllowed: false }; }
}

function permitted(value) {
  return value?.marketingAllowed === true && value?.saleOfDataAllowed === true;
}

function privacyApiPending(env) {
  try {
    const api = env.Shopify?.customerPrivacy;
    if (typeof api?.marketingAllowed === 'function' && typeof api?.saleOfDataAllowed === 'function') return false;
    const choice = typeof api?.currentVisitorConsent === 'function' ? api.currentVisitorConsent() : {};
    if (choice?.marketing === 'no' || choice?.sale_of_data === 'no') return false;
    for (const name of ['marketingAllowed', 'saleOfDataAllowed']) if (typeof api?.[name] === 'function' && api[name]() === false) return false;
    return true;
  } catch { return false; }
}

function readGenuineMatchingCookies(text) {
  if (typeof text !== 'string' || text.length > 32768) return {};
  const matching = {};
  for (const name of ['fbp', 'fbc']) {
    const values = text.split(';').map(part => part.trim()).filter(part => part.startsWith(`_${name}=`)).map(part => part.slice(name.length + 2));
    if (!values.length || new Set(values).size !== 1) continue;
    const pattern = name === 'fbp' ? /^fb\.[12]\.\d{10,16}\.\d{1,64}$/ : /^fb\.[12]\.\d{10,16}\.[A-Za-z0-9_-]{1,500}$/;
    if (pattern.test(values[0])) matching[name] = values[0];
  }
  return matching;
}

function validObservation({ now, sourceOrigin, userAgent, privacy, cartBinding }) {
  return Number.isSafeInteger(now) && /^https:\/\/(?:www\.)?ipshealth\.co\.za$/.test(sourceOrigin ?? '') && permitted(privacy) && /^[a-f0-9]{64}$/.test(cartBinding ?? '') &&
    typeof userAgent === 'string' && userAgent.length >= 8 && userAgent.length <= 512 && !/[\u0000-\u001f\u007f]/.test(userAgent);
}

function buildStorefrontContext({ now, sourceOrigin, userAgent, matching, privacy, cartBinding }) {
  if (!validObservation({ now, sourceOrigin, userAgent, privacy, cartBinding })) return null;
  if (!matching || !Object.keys(matching).length || Object.keys(matching).some(key => !['fbp', 'fbc'].includes(key))) return null;
  const checkedCookies = readGenuineMatchingCookies(Object.entries(matching).map(([key, value]) => `_${key}=${value}`).join('; '));
  if (JSON.stringify(checkedCookies) !== JSON.stringify(matching)) return null;
  const observedAt = new Date(now).toISOString();
  const result = { version: 1, shop: CONTEXT_SHOP, observationBasis: 'storefront_observation', observedAt,
    expiresAt: new Date(now + CONTEXT_TTL_MS).toISOString(), sourceUrl: `${sourceOrigin}/`, sourceUrlBasis: 'observed_origin_core_projection',
    userAgent, matching: { ...matching }, privacy: { marketingAllowed: true, saleOfDataAllowed: true, observedAt, source: 'shopify_customer_privacy_api' }, cartBinding };
  return new TextEncoder().encode(JSON.stringify(result)).byteLength <= MAX_CONTEXT_BYTES ? result : null;
}

function createStorefrontContextRuntime(env, configuration = {}, now = () => Date.now()) {
  const config = Object.freeze({ ...configuration });
  const enabled = config.enabled === true && config.policyReviewed === true && config.approvedStorefrontCapture === true && allowedHost(env);
  if (!enabled) return { status: 'disabled', start: async () => ({ status: 'disabled' }), refresh: async () => ({ status: 'disabled' }), dispose() {} };
  if (typeof env.fetch !== 'function' || typeof env.navigator?.locks?.request !== 'function' || typeof env.crypto?.subtle?.digest !== 'function' || typeof env.AbortController !== 'function') return { status: 'unavailable', start: async () => ({ status: 'held' }), refresh: async () => ({ status: 'held' }), dispose() {} };
  let disposed = false;
  let started;
  let serial = Promise.resolve();
  let revision = 0;
  let eventDenied = false;
  let lastSignature = null;
  let lastWrittenAt = 0;
  let lastWrittenValue = '';
  let activeRefresh;
  let lastResult = { status: 'starting', revision: 0, checkedAt: null, writeAcknowledgedAt: null };

  function inspect() {
    return { ...lastResult, revision, inFlight: activeRefresh?.revision === revision };
  }

  function invalidateReceipt(status) {
    lastResult = { status, revision, checkedAt: now(), writeAcknowledgedAt: null };
  }
  let unsubscribe;
  let interval;
  let ready = false;
  let startup;
  let startupClosed = false;
  const startupDelays = [500, 1500, 3000];
  const listeners = [];

  function cancelStartup(ownerRevision) {
    if (ownerRevision !== undefined && ownerRevision !== revision) return;
    startupClosed = true;
    if (startup?.timer !== undefined) env.clearTimeout(startup.timer);
    startup = undefined;
  }

  function startupCurrent(value) {
    const elapsed = now() - value.openedAt;
    return startup === value && !disposed && revision === value.revision && env.document.visibilityState === 'visible' && elapsed >= 0 && elapsed <= startupDelays.at(-1);
  }

  function scheduleStartup() {
    const value = startup;
    if (!value) return;
    if (!startupCurrent(value) || value.complete) { cancelStartup(); return; }
    while (value.next < startupDelays.length) {
      const dueAt = value.openedAt + startupDelays[value.next++];
      if (dueAt <= now()) continue;
      value.timer = env.setTimeout(() => {
        value.timer = undefined;
        if (!startupCurrent(value)) { cancelStartup(value.revision); return; }
        void queueRefresh({ retry: value });
      }, dueAt - now());
      return;
    }
    cancelStartup();
  }

  function currentPermission() {
    const value = readStorefrontPermission(env);
    return eventDenied ? { marketingAllowed: false, saleOfDataAllowed: false } : value;
  }

  function endpoint(path) {
    const root = env.Shopify?.routes?.root;
    if (!allowedHost(env) || typeof root !== 'string' || !/^\/(?:[A-Za-z0-9_-]+\/)*$/.test(root) || !['cart.js', 'cart/update.js'].includes(path)) throw new Error('cart_endpoint_unavailable');
    return new URL(root + path, env.location.origin).href;
  }

  async function request(path, options = {}, json = false) {
    const abort = new env.AbortController();
    let timer;
    const timeout = new Promise((_, reject) => { timer = env.setTimeout(() => { abort.abort(); reject(new Error('cart_timeout')); }, 3000); });
    try {
      return await Promise.race([timeout, (async () => {
        const response = await env.fetch(endpoint(path), { credentials: 'same-origin', mode: 'same-origin', redirect: 'error', referrerPolicy: 'same-origin', ...options, signal: abort.signal });
        if (!response.ok) throw new Error('cart_unavailable');
        return json ? response.json() : undefined;
      })()]);
    } finally { env.clearTimeout(timer); }
  }

  async function cartIdentity(value) {
    if (!Number.isInteger(value?.item_count) || value.item_count < 0 || typeof value.token !== 'string' || !value.token.length || value.token.length > 2048) throw new Error('cart_identity_unavailable');
    const digest = await env.crypto.subtle.digest('SHA-256', new TextEncoder().encode(`${CONTEXT_SHOP}:${value.token}`));
    return { itemCount: value.item_count, binding: Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join(''), context: value.attributes?.[CONTEXT_ATTRIBUTE] };
  }

  async function cart() {
    return cartIdentity(await request('cart.js', {}, true));
  }

  async function writeOwn(value) {
    return request('cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { [CONTEXT_ATTRIBUTE]: value } }) }, Boolean(value));
  }

  function acknowledgedForOwner(identity, captureAt, matching) {
    if (!lastWrittenValue || identity.itemCount < 1 || captureAt - lastWrittenAt < 0 || captureAt - lastWrittenAt >= 5 * 60 * 1000) return false;
    try {
      const existing = JSON.parse(lastWrittenValue);
      const observedAt = Date.parse(existing.observedAt), expiresAt = Date.parse(existing.expiresAt);
      return existing.version === 1 && existing.shop === CONTEXT_SHOP && existing.observationBasis === 'storefront_observation' &&
        existing.sourceUrl === `${env.location.origin}/` && existing.sourceUrlBasis === 'observed_origin_core_projection' &&
        existing.userAgent === env.navigator.userAgent && existing.cartBinding === identity.binding &&
        existing.privacy?.source === 'shopify_customer_privacy_api' && permitted(existing.privacy) &&
        existing.privacy.observedAt === existing.observedAt && Number.isFinite(observedAt) && Number.isFinite(expiresAt) &&
        observedAt <= captureAt && captureAt - observedAt < 5 * 60 * 1000 && expiresAt === observedAt + CONTEXT_TTL_MS && expiresAt > captureAt &&
        JSON.stringify(existing.matching) === JSON.stringify(matching) && Object.keys(matching).length > 0;
    } catch { return false; }
  }

  function reusableSnapshot(raw, identity, captureAt, matching) {
    return raw?.attributes?.[CONTEXT_ATTRIBUTE] === lastWrittenValue && acknowledgedForOwner(identity, captureAt, matching);
  }

  async function clearOwn() {
    invalidateReceipt('clearing');
    await writeOwn('');
    lastSignature = null;
    lastWrittenAt = 0;
    lastWrittenValue = '';
    return { status: 'cleared' };
  }

  async function work(mode) {
    if (disposed) return { status: 'disposed' };
    if (mode.cartRevision !== undefined && mode.cartRevision !== revision) return { status: 'cart_refresh_superseded' };
    if (mode.retry && !startupCurrent(mode.retry)) return { status: 'startup_cancelled' };
    const generation = revision;
    const cancel = () => cancelStartup(generation);
    // An unavailable API can be rechecked without reading matching cookies. Arqum 2026-10-18.
    if (!eventDenied && privacyApiPending(env)) {
      if (mode.initial && !startupClosed && env.document.visibilityState === 'visible') startup = { openedAt: now(), revision, binding: null, next: 0, complete: false, waitingForPrivacy: true };
      else if (!startup?.waitingForPrivacy) cancel();
      return clearOwn();
    }
    if (!permitted(currentPermission())) { cancel(); return clearOwn(); }
    const before = mode.checkoutCart ? await cartIdentity(mode.checkoutCart) : await cart();
    if (mode.retry && !startupCurrent(mode.retry)) return { status: 'startup_cancelled' };
    if (startup && startup.binding !== null && before.binding !== startup.binding) {
      cancel();
      if (mode.retry) return { status: 'startup_cart_changed' };
    }
    if (disposed || generation !== revision || !permitted(currentPermission()) || before.itemCount === 0) { cancel(); return clearOwn(); }
    const eligibility = { now: now(), sourceOrigin: env.location.origin, userAgent: env.navigator.userAgent, privacy: currentPermission(), cartBinding: before.binding };
    if (!validObservation(eligibility)) { cancel(); return clearOwn(); }
    if (startup?.waitingForPrivacy) { startup.binding = before.binding; startup.waitingForPrivacy = false; }
    if (mode.initial && !startupClosed && env.document.visibilityState === 'visible') startup = { openedAt: eligibility.now, revision, binding: before.binding, next: 0, complete: false };
    let matching;
    try { matching = readGenuineMatchingCookies(env.document.cookie); }
    catch { cancel(); return clearOwn(); }
    const captureAt = now();
    const envelope = buildStorefrontContext({ now: captureAt, sourceOrigin: env.location.origin, userAgent: env.navigator.userAgent, matching, privacy: currentPermission(), cartBinding: before.binding });
    if (!envelope || disposed || generation !== revision) {
      if (Object.keys(matching).length || disposed || generation !== revision || !permitted(currentPermission())) cancel();
      return clearOwn();
    }
    if (startup && matching.fbp && matching.fbc) startup.complete = true;
    const signature = JSON.stringify([envelope.sourceUrl, envelope.userAgent, envelope.matching, envelope.cartBinding]);
    // Reuse only our acknowledged value still present on the freshly read cart. Arqum 2026-10-28.
    if (mode.checkoutCart && reusableSnapshot(mode.checkoutCart, before, captureAt, matching)) return { status: 'context_snapshot_confirmed' };
    if (!mode.checkoutCart && before.context === lastWrittenValue && signature === lastSignature && captureAt - lastWrittenAt >= 0 && captureAt - lastWrittenAt < 5 * 60 * 1000) return { status: 'unchanged' };
    if (mode.retry && !startupCurrent(mode.retry)) return { status: 'startup_cancelled' };
    if (!permitted(currentPermission())) { cancel(); return clearOwn(); }
    const written = await writeOwn(JSON.stringify(envelope));
    if (disposed || generation !== revision || !permitted(currentPermission())) { cancel(); return clearOwn(); }
    let after;
    try {
      // Verify the native updated cart at acknowledgement without another GET. Arqum 2026-10-28.
      after = await cartIdentity(written);
      if (after.context === undefined) after = await cart();
    } catch {
      try { after = await cart(); }
      catch { cancel(); return clearOwn(); }
    }
    if (disposed || generation !== revision || !permitted(currentPermission()) || after.binding !== before.binding || after.itemCount === 0 || after.context !== JSON.stringify(envelope)) { cancel(); return clearOwn(); }
    lastSignature = signature;
    lastWrittenAt = captureAt;
    lastWrittenValue = JSON.stringify(envelope);
    return { status: 'context_write_acknowledged' };
  }

  function queueRefresh(mode = {}) {
    const ownerRevision = revision;
    // Join current-cart work instead of queueing a duplicate checkout refresh. Arqum 2026-10-28.
    if (activeRefresh?.revision === ownerRevision) return activeRefresh.promise;
    const active = { revision: ownerRevision, promise: null, mode, started: false };
    const queued = serial.then(async () => {
      if (disposed) return { status: 'disposed' };
      if (ownerRevision !== revision) return { status: 'refresh_superseded' };
      const abort = new env.AbortController();
      const timer = env.setTimeout(() => abort.abort(), 3000);
      try {
        return await env.navigator.locks.request(CONTEXT_WRITE_LOCK, { mode: 'exclusive', signal: abort.signal }, async lock => {
          env.clearTimeout(timer);
          if (ownerRevision !== revision) return { status: 'refresh_superseded' };
          if (!lock || lock.name !== CONTEXT_WRITE_LOCK || lock.mode !== 'exclusive') return { status: 'held' };
          active.started = true;
          return work(active.mode);
        });
      } finally { env.clearTimeout(timer); }
    }).catch(() => { cancelStartup(ownerRevision); return { status: 'held' }; }).then(result => {
      if (ownerRevision !== revision) return result;
      lastResult = { status: result.status, revision, checkedAt: now(), writeAcknowledgedAt: result.status === 'context_write_acknowledged' ? now() : ['context_snapshot_confirmed', 'unchanged'].includes(result.status) ? lastResult.writeAcknowledgedAt : null };
      if (result.status === 'held' || result.status === 'disposed') cancelStartup(ownerRevision);
      else if (startup?.complete) cancelStartup(ownerRevision);
      else if (mode.initial || mode.retry) scheduleStartup();
      return result;
    });
    active.promise = queued.finally(() => { if (activeRefresh === active) activeRefresh = undefined; });
    activeRefresh = active;
    serial = active.promise;
    return active.promise;
  }

  function refresh() { return queueRefresh(); }

  function prepareCheckout(freshCart) {
    if (disposed) return Promise.resolve({ status: 'disposed' });
    const ownerRevision = revision;
    const checkoutCart = freshCart && { item_count: freshCart.item_count, token: freshCart.token, attributes: { [CONTEXT_ATTRIBUTE]: freshCart.attributes?.[CONTEXT_ATTRIBUTE] } };
    const pending = activeRefresh?.revision === ownerRevision ? activeRefresh : null;
    if (pending && !pending.started) pending.mode.checkoutCart = checkoutCart;
    const preparation = pending ? pending.promise : queueRefresh({ checkoutCart });
    return preparation.then(async result => {
      if (disposed) return { status: 'disposed' };
      if (ownerRevision !== revision) return { status: 'refresh_superseded' };
      if (pending && ['unchanged', 'context_snapshot_confirmed'].includes(result.status)) result = await queueRefresh({ checkoutCart });
      if (disposed) return { status: 'disposed' };
      if (ownerRevision !== revision) return { status: 'refresh_superseded' };
      if (!['context_write_acknowledged', 'context_snapshot_confirmed', 'unchanged'].includes(result.status)) return result;
      try {
        // A joined acknowledgement must belong to this checkout's observed cart. Arqum 2026-10-28.
        const owner = await cartIdentity(checkoutCart);
        if (disposed) return { status: 'disposed' };
        if (ownerRevision !== revision) return { status: 'refresh_superseded' };
        const matching = readGenuineMatchingCookies(env.document.cookie);
        if (permitted(currentPermission()) && acknowledgedForOwner(owner, now(), matching)) return result;
      } catch {}
      if (disposed) return { status: 'disposed' };
      if (ownerRevision !== revision) return { status: 'refresh_superseded' };
      invalidateReceipt('checkout_context_not_current');
      return { status: 'checkout_context_not_current' };
    });
  }

  function cartChanged() {
    cancelStartup();
    revision += 1;
    invalidateReceipt('cart_changed');
    startupClosed = false;
    return ready ? queueRefresh({ initial: true, cartRevision: revision }) : Promise.resolve({ status: 'starting' });
  }

  async function loadPrivacy() {
    if (env.Shopify?.customerPrivacy) return;
    if (typeof env.Shopify?.loadFeatures !== 'function') return;
    await new Promise(resolve => {
      let done = false;
      const finish = () => { if (!done) { done = true; env.clearTimeout(timer); resolve(); } };
      const timer = env.setTimeout(finish, 3000);
      try { env.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], finish); }
      catch { finish(); }
    });
  }

  function listen(target, type, callback) {
    target.addEventListener(type, callback);
    listeners.push(() => target.removeEventListener(type, callback));
  }

  return {
    status: 'prepared_storefront_context',
    start() {
      if (started) return started;
      started = (async () => {
        listen(env.document, 'visitorConsentCollected', event => {
          cancelStartup();
          revision += 1;
          invalidateReceipt('consent_changed');
          const detail = event?.detail;
          if (detail?.marketingAllowed === false || detail?.saleOfDataAllowed === false) eventDenied = true;
          else if (detail?.marketingAllowed === true && detail?.saleOfDataAllowed === true) eventDenied = false;
          if (detail?.marketingAllowed === true && detail?.saleOfDataAllowed === true && permitted(currentPermission())) {
            startupClosed = false;
            void queueRefresh({ initial: true, cartRevision: revision });
          } else void refresh();
        });
        if (typeof config.subscribeCart === 'function') unsubscribe = config.subscribeCart(() => { void cartChanged(); });
        listen(env.document, 'visibilitychange', () => { if (env.document.visibilityState === 'visible') { if (ready) void refresh(); } else cancelStartup(); });
        if (typeof env.addEventListener === 'function') {
          listen(env, 'pageshow', event => { if (event?.persisted) cancelStartup(); if (ready) void refresh(); });
          listen(env, 'pagehide', () => cancelStartup());
        }
        await loadPrivacy();
        if (disposed) return { status: 'disposed' };
        ready = true;
        if (typeof env.setInterval === 'function') interval = env.setInterval(() => { if (env.document.visibilityState === 'visible') void refresh(); }, 5 * 60 * 1000);
        return queueRefresh({ initial: true });
      })().catch(() => ({ status: 'held' }));
      return started;
    },
    refresh,
    prepareCheckout,
    inspect,
    cartChanged,
    dispose() {
      disposed = true;
      cancelStartup();
      revision += 1;
      invalidateReceipt('disposed');
      for (const remove of listeners) remove();
      if (typeof unsubscribe === 'function') unsubscribe();
      if (interval !== undefined) env.clearInterval(interval);
    }
  };
}

if (typeof window === 'undefined' || window.IPSConfirmedMetaContext) return;
const subscribeCart = typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' ? callback => subscribe(PUB_SUB_EVENTS.cartUpdate, callback) : undefined;
const runtime = createStorefrontContextRuntime(window, {enabled:true,policyReviewed:true,approvedStorefrontCapture:true,subscribeCart});
window.IPSConfirmedMetaContext = runtime;
void runtime.start().catch(() => {});
})();
